import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTrendingHashtags } from '../services/hashtagService';
import { HashtagIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface TrendingHashtag {
  id: string;
  tag: string;
  count: number;
  score: number;
  postCount: number;
}

const ExploreHashtagsPage: React.FC = () => {
  const [hashtags, setHashtags] = useState<TrendingHashtag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useTranslation();

  useEffect(() => {
    const fetchTrendingHashtags = async () => {
      try {
        setLoading(true);
        // Obtener más hashtags para esta página (20 en lugar de 5)
        const trendingHashtags = await getTrendingHashtags(20);
        setHashtags(trendingHashtags);
      } catch (error) {
        console.error('Error fetching trending hashtags:', error);
        setError('No se pudieron cargar las tendencias');
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingHashtags();
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <Helmet>
        <title>Explorar Hashtags | DomiRed</title>
        <meta name="description" content="Explora los hashtags en tendencia en DomiRed" />
      </Helmet>

      <div className="mb-6">
        <Link to="/" className="text-domired-600 dark:text-domired-400 hover:underline mb-4 inline-block">
          &larr; Volver al inicio
        </Link>

        <div className="flex items-center mt-2">
          <HashtagIcon className="h-6 w-6 mr-2 text-domired-600 dark:text-domired-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Hashtags en tendencia</h1>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-domired-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-md">
          {error}
        </div>
      ) : hashtags.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No hay tendencias disponibles en este momento.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {hashtags.map((hashtag) => (
            <Link
              key={hashtag.id}
              to={`/hashtag/${hashtag.id}`}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <HashtagIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
                <span className="text-lg font-medium text-domired-600 dark:text-domired-400">
                  {hashtag.tag}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {hashtag.count} {hashtag.count === 1 ? 'publicación' : 'publicaciones'}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExploreHashtagsPage;
